/**
 * Repo RAG Service – Perplexity-grade hybrid search over attached GitHub repo chunks.
 *
 * Pipeline:
 *   Pre-flight: whitelist filter on tree metadata (no wasted content fetches)
 *   Pass 1 (immediate): embed tree map + README → signal onReady so user can chat
 *   Pass 2 (background): extract signatures + chunk code → true batch embed → store
 *
 * Retrieval: 3-pass Find → Filter → Feed (tree/signature search, then code body search, then FTS)
 */

import { supabase, getCurrentUserId, isSupabaseConfigured } from '@/lib/supabase';
import { embeddingService } from './embeddingService';
import * as githubService from './githubService';
import { indexSymbolsAndGraphBatch } from './repoSymbolService';
import { clearRepoGraphForUser, clearRepoSymbolsForUser } from './repoGraphService';
import {
  ALLOW_EXT,
  SKIP_DIRS,
  SKIP_FILES,
  SMALL_FILE_THRESHOLD,
  MAX_CHUNKS_PER_FILE,
  shouldSkipPath as sharedShouldSkipPath,
  fileExtension,
} from '../../shared/repoIndexingConstants';

const API_BASE = '';

// Re-export for any callers that imported from here
export { ALLOW_EXT, SKIP_DIRS, SKIP_FILES, SMALL_FILE_THRESHOLD, MAX_CHUNKS_PER_FILE };
const FETCH_BATCH_SIZE = 12;
const EMBED_BATCH_SIZE = 10;         // true batch: 10 texts per single API call
const EMBED_DELAY_MS = 200;          // only between batch groups, not after every text
const STORE_CHUNK_BATCH_SIZE = 12;   // keep each store-chunks POST under body limits (local + Vercel)
const EXPECTED_EMBEDDING_DIM = 4000;
const CLIENT_VECTOR_SEARCH_MAX_CHUNKS = 200;

let repoVectorRpcUnavailableLogged = false;

export interface RepoChunkResult {
  owner?: string;
  repo?: string;
  file_path: string;
  chunk_index: number;
  chunk_text: string;
  similarity: number;
  metadata?: Record<string, unknown>;
}

/** Resolve chunk layer: metadata, DB column, or text heuristics (handles legacy mismatches). */
export function getChunkType(
  r: Pick<RepoChunkResult, 'chunk_text' | 'metadata' | 'file_path'> & { chunk_type?: string }
): string {
  const meta = r.metadata?.chunk_type;
  if (typeof meta === 'string' && meta.trim()) return meta.trim();
  const col = r.chunk_type;
  if (col && col !== 'chunk') return col;
  if (r.chunk_text.includes('[SIGNATURES]')) return 'signature';
  if (r.file_path === '[REPO_TREE]' || r.chunk_text.includes('[REPO_TREE]')) return 'tree';
  return col ?? 'chunk';
}

function rrfChunkKey(r: RepoChunkResult): string {
  return `${r.file_path}::${getChunkType(r)}::${r.chunk_index}`;
}

export interface RepoPipelineProgress {
  batchIndex: number;
  totalBatches: number;
  chunksIndexed: number;
  percent: number;
}

export interface RepoPipelineReady {
  info: import('./githubService').GitHubRepoInfo;
  readme: string | undefined;
  tree: import('./githubService').GitHubTree;
}

// ── Filter helpers ───────────────────────────────────────────────────────────

function shouldSkipPath(path: string): boolean {
  return sharedShouldSkipPath(path);
}

// ── Language / file-type detection ──────────────────────────────────────────

function detectLanguage(filePath: string): string {
  const ext = fileExtension(filePath);
  const map: Record<string, string> = {
    '.ts': 'typescript', '.tsx': 'typescript',
    '.js': 'javascript', '.jsx': 'javascript', '.mjs': 'javascript', '.cjs': 'javascript',
    '.py': 'python', '.go': 'go', '.rs': 'rust', '.rb': 'ruby',
    '.java': 'java', '.kt': 'kotlin', '.swift': 'swift',
    '.c': 'c', '.cpp': 'cpp', '.h': 'c',
    '.md': 'markdown', '.mdx': 'markdown',
    '.json': 'json', '.yaml': 'yaml', '.yml': 'yaml',
    '.sh': 'shell', '.bash': 'shell', '.zsh': 'shell',
    '.css': 'css', '.scss': 'scss', '.html': 'html', '.sql': 'sql',
  };
  return map[ext] ?? 'text';
}

function classifyFileType(filePath: string): string {
  const lower = filePath.toLowerCase();
  if (lower.includes('.test.') || lower.includes('.spec.') || lower.includes('__tests__') || lower.includes('/test/') || lower.includes('/tests/')) return 'test';
  if (lower.includes('/controllers/') || lower.endsWith('controller.ts') || lower.endsWith('controller.js')) return 'controller';
  if (lower.includes('/services/') || lower.endsWith('service.ts') || lower.endsWith('service.js')) return 'service';
  if (lower.includes('/routes/') || lower.endsWith('routes.ts') || lower.endsWith('routes.js') || lower.endsWith('router.ts') || lower.endsWith('router.js')) return 'route';
  if (lower.includes('/middleware/') || lower.endsWith('middleware.ts') || lower.endsWith('middleware.js')) return 'middleware';
  if (lower.includes('/models/') || lower.includes('/schemas/') || lower.endsWith('model.ts') || lower.endsWith('schema.ts')) return 'model';
  if (lower.includes('/utils/') || lower.includes('/helpers/') || lower.includes('/lib/')) return 'util';
  if (lower.endsWith('.config.ts') || lower.endsWith('.config.js') || lower.endsWith('.json') || lower.endsWith('.yaml') || lower.endsWith('.yml') || lower.endsWith('.env.example')) return 'config';
  if (lower.endsWith('readme.md') || lower.endsWith('readme.mdx')) return 'readme';
  return 'source';
}

// ── Signature extraction ─────────────────────────────────────────────────────

function extractSignatures(content: string, language: string): string {
  const lines = content.split('\n');
  const sigLines: string[] = [];

  const patterns: RegExp[] = [];
  if (language === 'typescript' || language === 'javascript') {
    patterns.push(
      /^(export\s+)?(async\s+)?function\s+\w+/,
      /^(export\s+)?(const|let|var)\s+\w+\s*=\s*(async\s*)?\(/,
      /^(export\s+)?(const|let|var)\s+\w+\s*:\s*\w/,
      /^(export\s+)?(default\s+)?(class|interface|type|enum)\s+\w+/,
      /^\s*(public|private|protected|static|async)\s+\w+\s*\(/,
      /^(export\s+)?default\s+/,
      /^module\.exports/,
      /^exports\.\w+/,
    );
  } else if (language === 'python') {
    patterns.push(/^(async\s+)?def\s+\w+/, /^class\s+\w+/);
  } else if (language === 'go') {
    patterns.push(/^func\s+/, /^type\s+\w+\s+struct/, /^type\s+\w+\s+interface/);
  } else if (language === 'rust') {
    patterns.push(/^pub\s+(fn|struct|enum|trait|impl)\s+/, /^fn\s+\w+/);
  } else if (language === 'java' || language === 'kotlin') {
    patterns.push(/^\s*(public|private|protected|static).*\(/, /^(class|interface|enum)\s+\w+/);
  }

  for (const line of lines) {
    if (patterns.some(p => p.test(line))) {
      const trimmed = line.trim();
      if (trimmed) sigLines.push(trimmed);
    }
  }
  return sigLines.join('\n');
}

// ── Import extraction (for metadata breadcrumb) ──────────────────────────────

function extractImports(content: string, language: string): string[] {
  const deps: string[] = [];
  if (language === 'typescript' || language === 'javascript') {
    const importRe = /^import\s+.*?from\s+['"]([^'"]+)['"]/gm;
    const requireRe = /require\s*\(\s*['"]([^'"]+)['"]\s*\)/g;
    let m: RegExpExecArray | null;
    while ((m = importRe.exec(content)) !== null) deps.push(m[1]);
    while ((m = requireRe.exec(content)) !== null) deps.push(m[1]);
  } else if (language === 'python') {
    const pyRe = /^(?:from|import)\s+([\w.]+)/gm;
    let m: RegExpExecArray | null;
    while ((m = pyRe.exec(content)) !== null) deps.push(m[1]);
  }
  return [...new Set(deps)];
}

// ── Smart chunker with path injection ───────────────────────────────────────

function chunkCode(
  content: string,
  filePath: string,
  maxChunkSize = 2000,
  overlapSize = 150,
  maxChunks = MAX_CHUNKS_PER_FILE
): { text: string; index: number }[] {
  if (!content || content.trim().length === 0) return [];

  // Small file exception: keep as one chunk so LLM sees imports + exports together
  const byteSize = new TextEncoder().encode(content).length;
  if (byteSize <= SMALL_FILE_THRESHOLD) {
    return [{ text: `// File: ${filePath}\n${content.trim()}`, index: 0 }];
  }

  const chunks: { text: string; index: number }[] = [];
  let start = 0;
  let index = 0;

  while (start < content.length && index < maxChunks) {
    let end = Math.min(start + maxChunkSize, content.length);
    if (end < content.length) {
      // Prefer breaking at a blank line (between logical blocks)
      const blankLine = content.lastIndexOf('\n\n', end);
      if (blankLine > start + maxChunkSize / 2) {
        end = blankLine + 2;
      } else {
        // Fall back to next newline
        const nextNewline = content.indexOf('\n', end);
        if (nextNewline !== -1 && nextNewline - start <= maxChunkSize + 200) end = nextNewline + 1;
      }
    }
    const slice = content.slice(start, end).trim();
    // Inject file path into every chunk so LLM always knows the source
    if (slice) chunks.push({ text: `// File: ${filePath}\n${slice}`, index: index++ });
    start = end - (end < content.length ? overlapSize : 0);
    if (start <= 0) start = end;
  }
  return chunks;
}

// ── Auth helpers ─────────────────────────────────────────────────────────────

async function getAuthHeaders(): Promise<HeadersInit> {
  const { getCurrentSession } = await import('@/lib/supabase');
  const session = await getCurrentSession();
  if (!session) throw new Error('Not authenticated');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`,
  };
}

// ── Store chunks with full metadata ──────────────────────────────────────────

async function storeChunksWithMetadata(
  owner: string,
  repo: string,
  chunks: Array<{
    file_path: string;
    chunk_index: number;
    chunk_text: string;
    embedding: number[];
    metadata: Record<string, unknown>;
  }>
): Promise<void> {
  if (chunks.length === 0) return;
  const headers = await getAuthHeaders();

  for (let i = 0; i < chunks.length; i += STORE_CHUNK_BATCH_SIZE) {
    const batch = chunks.slice(i, i + STORE_CHUNK_BATCH_SIZE);
    const res = await fetch(`${API_BASE}/api/github/repos/${owner}/${repo}/store-chunks`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ chunks: batch }),
    });
    if (!res.ok) {
      const text = await res.text();
      let errMsg: string;
      try { errMsg = JSON.parse(text)?.error ?? text; } catch { errMsg = text; }
      throw new Error(`Failed to store repo chunks: ${res.status} ${errMsg}`);
    }
  }
}

// ── Embed with retry ──────────────────────────────────────────────────────────

async function batchEmbedWithRetry(texts: string[]): Promise<{ embedding: number[] }[]> {
  try {
    return await embeddingService.generateEmbeddingsBatch(texts, { maxBatchSize: EMBED_BATCH_SIZE, delayMs: EMBED_DELAY_MS });
  } catch (err) {
    console.warn('[Repo RAG] Batch embed failed, retrying once in 3s:', err);
    await new Promise(r => setTimeout(r, 3000));
    return await embeddingService.generateEmbeddingsBatch(texts, { maxBatchSize: EMBED_BATCH_SIZE, delayMs: EMBED_DELAY_MS });
  }
}

/**
 * Main indexing pipeline.
 *
 * Pass 1 (seconds): embed tree map + README → store as chunk_type='tree' → call onReady
 * Pass 2 (background): per file batch → signatures (chunk_type='signature') + code chunks (chunk_type='chunk')
 */
export async function indexRepoPipeline(
  owner: string,
  repo: string,
  callbacks: {
    onReady?: (data: RepoPipelineReady) => void;
    onProgress?: (data: RepoPipelineProgress) => void;
  } = {}
): Promise<{ chunksIndexed: number }> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');

  const [info, tree, readmeData] = await Promise.all([
    githubService.getRepoInfo(owner, repo),
    githubService.getRepoTree(owner, repo),
    githubService.getReadme(owner, repo).catch(() => ({ content: undefined })),
  ]);
  const readme = typeof (readmeData as { content?: string })?.content === 'string'
    ? (readmeData as { content: string }).content
    : undefined;

  // Filter to only whitelisted source files — runs against tree metadata, no content fetches wasted
  const paths = tree.tree
    .filter((item: { type: string; path: string }) => item.type === 'blob' && !shouldSkipPath(item.path))
    .map((item: { path: string }) => item.path);

  // Clear existing index (prevents duplicates on re-index)
  await Promise.all([
    supabase
      .from('github_repo_chunks')
      .delete()
      .eq('user_id', userId)
      .eq('owner', owner)
      .eq('repo', repo),
    clearRepoSymbolsForUser(owner, repo),
    clearRepoGraphForUser(owner, repo),
  ]);

  let totalChunksIndexed = 0;

  // ── PASS 1: Tree Map + README (immediate, user can start chatting) ─────────
  const treeText = `// File: [REPO_TREE]\nRepository: ${owner}/${repo}\n\nFile structure:\n${paths.join('\n')}`;
  const pass1Texts: string[] = [treeText];
  if (readme) pass1Texts.push(`// File: README.md\n${readme.slice(0, 6000)}`);

  try {
    const pass1Embeddings = await batchEmbedWithRetry(pass1Texts);
    const pass1Chunks = pass1Texts
      .map((text, i) => ({
        file_path: i === 0 ? '[REPO_TREE]' : 'README.md',
        chunk_index: 0,
        chunk_text: text,
        embedding: pass1Embeddings[i]?.embedding ?? [],
        metadata: { chunk_type: 'tree', language: 'text', file_type: 'config' } as Record<string, unknown>,
      }))
      .filter(c => c.embedding.length === EXPECTED_EMBEDDING_DIM);

    if (pass1Chunks.length > 0) {
      await storeChunksWithMetadata(owner, repo, pass1Chunks);
      totalChunksIndexed += pass1Chunks.length;
    }
  } catch (err) {
    console.warn('[Repo RAG] Pass 1 (tree/README) embed failed:', err);
  }

  // Signal ready — user can chat immediately while Pass 2 runs in the background
  callbacks.onReady?.({ info, readme, tree });

  if (paths.length === 0) return { chunksIndexed: totalChunksIndexed };

  // ── PASS 2: Signatures + Code Chunks (background) ─────────────────────────
  const totalBatches = Math.ceil(paths.length / FETCH_BATCH_SIZE);

  for (let b = 0; b < totalBatches; b++) {
    const batchPaths = paths.slice(b * FETCH_BATCH_SIZE, (b + 1) * FETCH_BATCH_SIZE);
    const batchResults = await githubService.batchGetFiles(owner, repo, batchPaths);

    const sigChunks: Array<{ file_path: string; chunk_index: number; chunk_text: string; metadata: Record<string, unknown> }> = [];
    const codeChunks: Array<{ file_path: string; chunk_index: number; chunk_text: string; metadata: Record<string, unknown> }> = [];

    for (const [path, result] of Object.entries(batchResults)) {
      if (!result || 'error' in result) continue;
      const content = (result as { content: string }).content ?? '';
      if (!content.trim()) continue;

      const language = detectLanguage(path);
      const fileType = classifyFileType(path);
      const deps = extractImports(content, language);
      const byteSize = new TextEncoder().encode(content).length;
      const isSmallFile = byteSize <= SMALL_FILE_THRESHOLD;
      const baseMeta: Record<string, unknown> = {
        language,
        file_type: fileType,
        dependencies: deps,
        size_bytes: byteSize,
        is_small_file: isSmallFile,
      };

      // Level 2: Signature skeleton
      const signatures = extractSignatures(content, language);
      if (signatures.trim()) {
        sigChunks.push({
          file_path: path,
          chunk_index: 0,
          chunk_text: `// File: ${path} [SIGNATURES]\n${signatures}`,
          metadata: { ...baseMeta, chunk_type: 'signature' },
        });
      }

      // Level 3: Code body chunks
      const fileChunks = chunkCode(content, path);
      for (const c of fileChunks) {
        codeChunks.push({
          file_path: path,
          chunk_index: c.index,
          chunk_text: c.text,
          metadata: { ...baseMeta, chunk_type: 'chunk' },
        });
      }
    }

    // Embed signatures and code chunks together in one batch call per file group
    const allChunks = [...sigChunks, ...codeChunks];
    if (allChunks.length > 0) {
      try {
        const texts = allChunks.map(x => x.chunk_text);
        const embeddings = await batchEmbedWithRetry(texts);

        const toStore = allChunks
          .map((x, i) => ({ ...x, embedding: embeddings[i]?.embedding ?? [] }))
          .filter(c => c.embedding.length === EXPECTED_EMBEDDING_DIM);

        if (toStore.length > 0) {
          await storeChunksWithMetadata(owner, repo, toStore);
          totalChunksIndexed += toStore.length;
        }
      } catch (err) {
        console.error(`[Repo RAG] Pass 2 batch ${b + 1} failed (skipping):`, err);
      }
    }

    // Pass 2.5: AST symbols + graph edges (server-side ts-morph)
    const symbolFiles: Array<{ path: string; content: string }> = [];
    for (const [path, result] of Object.entries(batchResults)) {
      if (!result || 'error' in result) continue;
      const content = (result as { content: string }).content ?? '';
      if (content.trim()) symbolFiles.push({ path, content });
    }
    if (symbolFiles.length > 0) {
      try {
        await indexSymbolsAndGraphBatch(owner, repo, symbolFiles, paths);
      } catch (err) {
        console.warn(`[Repo RAG] Pass 2.5 batch ${b + 1} failed (skipping):`, err);
      }
    }

    callbacks.onProgress?.({
      batchIndex: b + 1,
      totalBatches,
      chunksIndexed: totalChunksIndexed,
      percent: Math.round(((b + 1) / totalBatches) * 100),
    });
  }

  return { chunksIndexed: totalChunksIndexed };
}

/**
 * Force a full re-index (clears stale paths, re-embeds from GitHub).
 * Same as indexRepoPipeline; exposed for UI "Refresh index".
 */
export async function refreshRepoIndex(
  owner: string,
  repo: string,
  callbacks: {
    onReady?: (data: RepoPipelineReady) => void;
    onProgress?: (data: RepoPipelineProgress) => void;
  } = {}
): Promise<{ chunksIndexed: number }> {
  return indexRepoPipeline(owner, repo, callbacks);
}

function buildChunksForFileContent(path: string, content: string): Array<{
  file_path: string;
  chunk_index: number;
  chunk_text: string;
  metadata: Record<string, unknown>;
}> {
  if (!content.trim()) return [];

  const language = detectLanguage(path);
  const fileType = classifyFileType(path);
  const deps = extractImports(content, language);
  const byteSize = new TextEncoder().encode(content).length;
  const baseMeta: Record<string, unknown> = {
    language,
    file_type: fileType,
    dependencies: deps,
    size_bytes: byteSize,
    is_small_file: byteSize <= SMALL_FILE_THRESHOLD,
  };

  const out: Array<{
    file_path: string;
    chunk_index: number;
    chunk_text: string;
    metadata: Record<string, unknown>;
  }> = [];

  const signatures = extractSignatures(content, language);
  if (signatures.trim()) {
    out.push({
      file_path: path,
      chunk_index: 0,
      chunk_text: `// File: ${path} [SIGNATURES]\n${signatures}`,
      metadata: { ...baseMeta, chunk_type: 'signature' },
    });
  }

  for (const c of chunkCode(content, path)) {
    out.push({
      file_path: path,
      chunk_index: c.index,
      chunk_text: c.text,
      metadata: { ...baseMeta, chunk_type: 'chunk' },
    });
  }
  return out;
}

/** Resolve repo tree paths for symbol parsing — index-first, GitHub last resort. */
async function resolveRepoTreePathsForSymbols(owner: string, repo: string): Promise<string[]> {
  const fromIndex = await getRepoTreePathsFromIndex(owner, repo);
  if (fromIndex.length > 0) return fromIndex;

  const fromChunks = await listIndexedFilePaths(owner, repo);
  if (fromChunks.length > 0) return fromChunks;

  try {
    const tree = await githubService.getRepoTree(owner, repo);
    return tree.tree.filter(i => i.type === 'blob').map(i => i.path);
  } catch {
    return [];
  }
}

export interface ReindexFilesResult {
  chunksIndexed: number;
  symbolsStored: number;
  paths: string[];
}

/**
 * Re-embed specific file paths from GitHub (default branch).
 */
export async function reindexFilePaths(
  owner: string,
  repo: string,
  filePaths: string[]
): Promise<ReindexFilesResult> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');

  const paths = [...new Set(filePaths.map(p => p.trim()).filter(Boolean))];
  if (paths.length === 0) return { chunksIndexed: 0, symbolsStored: 0, paths: [] };

  await supabase
    .from('github_repo_chunks')
    .delete()
    .eq('user_id', userId)
    .eq('owner', owner)
    .eq('repo', repo)
    .in('file_path', paths);

  const batchResults = await githubService.batchGetFiles(owner, repo, paths);
  const allChunks: Array<{
    file_path: string;
    chunk_index: number;
    chunk_text: string;
    metadata: Record<string, unknown>;
  }> = [];

  for (const path of paths) {
    const result = batchResults[path];
    if (!result || 'error' in result) continue;
    const content = (result as { content: string }).content ?? '';
    allChunks.push(...buildChunksForFileContent(path, content));
  }

  if (allChunks.length === 0) return { chunksIndexed: 0, symbolsStored: 0, paths };

  const embeddings = await batchEmbedWithRetry(allChunks.map(x => x.chunk_text));
  const toStore = allChunks
    .map((x, i) => ({ ...x, embedding: embeddings[i]?.embedding ?? [] }))
    .filter(c => c.embedding.length === EXPECTED_EMBEDDING_DIM);

  if (toStore.length > 0) {
    await storeChunksWithMetadata(owner, repo, toStore);
  }

  const symbolFiles: Array<{ path: string; content: string }> = [];
  for (const path of paths) {
    const result = batchResults[path];
    if (!result || 'error' in result) continue;
    const content = (result as { content: string }).content ?? '';
    if (content.trim()) symbolFiles.push({ path, content });
  }

  let symbolsStored = 0;
  if (symbolFiles.length > 0) {
    try {
      const repoTreePaths = await resolveRepoTreePathsForSymbols(owner, repo);
      const symResult = await indexSymbolsAndGraphBatch(owner, repo, symbolFiles, repoTreePaths, paths);
      symbolsStored = symResult.symbolsStored;
    } catch (err) {
      console.warn('[Repo RAG] reindexFilePaths symbol/graph update failed:', err);
    }
  }

  return { chunksIndexed: toStore.length, symbolsStored, paths };
}

/**
 * Re-embed files from in-memory content (e.g. after AI edit push — updates RAG immediately).
 */
export async function reindexFilesFromContent(
  owner: string,
  repo: string,
  files: Array<{ file_path: string; content: string }>
): Promise<ReindexFilesResult> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');

  const normalized = files
    .map(f => ({ file_path: f.file_path.trim(), content: f.content }))
    .filter(f => f.file_path);
  if (normalized.length === 0) return { chunksIndexed: 0, symbolsStored: 0, paths: [] };

  const paths = normalized.map(f => f.file_path);
  await supabase
    .from('github_repo_chunks')
    .delete()
    .eq('user_id', userId)
    .eq('owner', owner)
    .eq('repo', repo)
    .in('file_path', paths);

  const allChunks = normalized.flatMap(f => buildChunksForFileContent(f.file_path, f.content));
  if (allChunks.length === 0) return { chunksIndexed: 0, symbolsStored: 0, paths };

  const embeddings = await batchEmbedWithRetry(allChunks.map(x => x.chunk_text));
  const toStore = allChunks
    .map((x, i) => ({ ...x, embedding: embeddings[i]?.embedding ?? [] }))
    .filter(c => c.embedding.length === EXPECTED_EMBEDDING_DIM);

  if (toStore.length > 0) {
    await storeChunksWithMetadata(owner, repo, toStore);
  }

  let symbolsStored = 0;
  try {
    const repoTreePaths = await resolveRepoTreePathsForSymbols(owner, repo);
    const symResult = await indexSymbolsAndGraphBatch(
      owner,
      repo,
      normalized.map(f => ({ path: f.file_path, content: f.content })),
      repoTreePaths,
      paths
    );
    symbolsStored = symResult.symbolsStored;
  } catch (err) {
    console.warn('[Repo RAG] reindexFilesFromContent symbol/graph update failed:', err);
  }

  return { chunksIndexed: toStore.length, symbolsStored, paths };
}

function parseStoredEmbedding(raw: unknown): number[] | null {
  if (Array.isArray(raw)) {
    return raw.every(v => typeof v === 'number') ? (raw as number[]) : null;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed) && parsed.every(v => typeof v === 'number')) {
        return parsed as number[];
      }
    } catch {
      // fall through
    }
  }
  return null;
}

function cosineSimilarity(a: number[], b: number[]): number {
  const len = Math.min(a.length, b.length);
  if (len === 0) return 0;
  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < len; i++) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  if (normA === 0 || normB === 0) return 0;
  return dot / (Math.sqrt(normA) * Math.sqrt(normB));
}

function keywordOverlapScore(chunkText: string, searchQuery?: string): number {
  if (!searchQuery?.trim()) return 0;
  const terms = searchQuery
    .toLowerCase()
    .match(/\b[a-z0-9_]{3,}\b/g);
  if (!terms?.length) return 0;
  const lower = chunkText.toLowerCase();
  return terms.reduce((score, term) => score + (lower.includes(term) ? 1 : 0), 0);
}

async function getRepoChunkCount(userId: string, owner: string, repo: string): Promise<number> {
  const { count, error } = await supabase
    .from('github_repo_chunks')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('owner', owner)
    .eq('repo', repo);
  if (error) return 0;
  return count ?? 0;
}

async function matchRepoChunksClientSide(
  embedding: number[],
  userId: string,
  owner: string,
  repo: string,
  chunkTypes: string[],
  matchThreshold: number,
  matchCount: number
): Promise<RepoChunkResult[]> {
  if (!Array.isArray(embedding) || embedding.length === 0) return [];

  const { data, error } = await supabase
    .from('github_repo_chunks')
    .select('file_path, chunk_index, chunk_text, metadata, chunk_type, embedding')
    .eq('user_id', userId)
    .eq('owner', owner)
    .eq('repo', repo)
    .limit(CLIENT_VECTOR_SEARCH_MAX_CHUNKS);

  if (error || !data?.length) return [];

  const typeSet = new Set(chunkTypes);
  const scored: RepoChunkResult[] = [];

  for (const row of data as Record<string, unknown>[]) {
    const chunkType = String(row.chunk_type ?? getChunkType(row as RepoChunkResult));
    if (typeSet.size > 0 && !typeSet.has(chunkType)) continue;

    const stored = parseStoredEmbedding(row.embedding);
    if (!stored || stored.length !== embedding.length) continue;

    const similarity = cosineSimilarity(embedding, stored);
    if (similarity <= matchThreshold) continue;

    scored.push({
      file_path: row.file_path as string,
      chunk_index: row.chunk_index as number,
      chunk_text: row.chunk_text as string,
      similarity,
      metadata: row.metadata as Record<string, unknown>,
    });
  }

  if (scored.length === 0) {
    for (const row of data as Record<string, unknown>[]) {
      const chunkType = String(row.chunk_type ?? 'chunk');
      if (typeSet.size > 0 && !typeSet.has(chunkType)) continue;
      const stored = parseStoredEmbedding(row.embedding);
      if (!stored || stored.length !== embedding.length) continue;
      scored.push({
        file_path: row.file_path as string,
        chunk_index: row.chunk_index as number,
        chunk_text: row.chunk_text as string,
        similarity: cosineSimilarity(embedding, stored),
        metadata: row.metadata as Record<string, unknown>,
      });
    }
  }

  return scored
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, matchCount);
}

async function matchRepoChunksByType(
  embedding: number[],
  userId: string,
  owner: string,
  repo: string,
  chunkTypes: string[],
  matchThreshold: number,
  matchCount: number,
  searchQuery?: string
): Promise<RepoChunkResult[]> {
  if (!Array.isArray(embedding) || embedding.length === 0) {
    return fetchRepoChunksFallback(
      userId,
      owner,
      repo,
      chunkTypes,
      matchCount,
      (row) => ({
        file_path: row.file_path as string,
        chunk_index: row.chunk_index as number,
        chunk_text: row.chunk_text as string,
        similarity: (row.similarity as number) ?? 0,
        metadata: row.metadata as Record<string, unknown>,
      }),
      searchQuery
    );
  }

  const toResult = (row: Record<string, unknown>): RepoChunkResult => ({
    file_path: row.file_path as string,
    chunk_index: row.chunk_index as number,
    chunk_text: row.chunk_text as string,
    similarity: (row.similarity as number) ?? (row.rank as number) ?? 0,
    metadata: row.metadata as Record<string, unknown>,
  });

  const chunkCount = await getRepoChunkCount(userId, owner, repo);

  if (chunkCount > 0 && chunkCount <= CLIENT_VECTOR_SEARCH_MAX_CHUNKS) {
    const clientResults = await matchRepoChunksClientSide(
      embedding,
      userId,
      owner,
      repo,
      chunkTypes,
      matchThreshold,
      matchCount
    );
    if (clientResults.length > 0) return clientResults;
  }

  const embeddingPayload = JSON.stringify(embedding);
  const rpcParams = {
    query_embedding: embeddingPayload,
    filter_user_id: userId,
    filter_owner: owner,
    filter_repo: repo,
    filter_chunk_types: chunkTypes,
    match_threshold: matchThreshold,
    match_count: matchCount,
  };

  const { data, error } = await supabase.rpc('match_github_repo_chunks_by_type_v2', rpcParams);

  if (!error && data?.length) {
    return (data as Record<string, unknown>[]).map(toResult);
  }

  if (chunkCount > 0 && chunkCount <= CLIENT_VECTOR_SEARCH_MAX_CHUNKS) {
    return matchRepoChunksClientSide(
      embedding,
      userId,
      owner,
      repo,
      chunkTypes,
      matchThreshold,
      matchCount
    );
  }

  if (error && !repoVectorRpcUnavailableLogged) {
    repoVectorRpcUnavailableLogged = true;
    console.warn('[Repo RAG] vector RPC unavailable, using table/keyword fallback:', error.message);
  }

  return fetchRepoChunksFallback(
    userId,
    owner,
    repo,
    chunkTypes,
    matchCount,
    toResult,
    searchQuery
  );
}

/** Last-resort retrieval when vector RPCs are unavailable. */
async function fetchRepoChunksFallback(
  userId: string,
  owner: string,
  repo: string,
  chunkTypes: string[],
  matchCount: number,
  toResult: (row: Record<string, unknown>) => RepoChunkResult,
  searchQuery?: string
): Promise<RepoChunkResult[]> {
  const typeSet = new Set(chunkTypes);
  const { data, error } = await supabase
    .from('github_repo_chunks')
    .select('file_path, chunk_index, chunk_text, metadata, chunk_type')
    .eq('user_id', userId)
    .eq('owner', owner)
    .eq('repo', repo)
    .in('chunk_type', chunkTypes.length > 0 ? chunkTypes : ['chunk', 'signature', 'tree'])
    .limit(Math.max(matchCount * 3, 30));

  if (error || !data?.length) {
    if (error) console.warn('[Repo RAG] table fallback failed:', error.message);
    return [];
  }

  const typePriority: Record<string, number> = { chunk: 3, signature: 2, tree: 1 };

  return (data as Record<string, unknown>[])
    .filter(row => typeSet.size === 0 || typeSet.has(String(row.chunk_type ?? 'chunk')))
    .map(row => {
      const chunkType = String(row.chunk_type ?? 'chunk');
      const kwScore = keywordOverlapScore(String(row.chunk_text ?? ''), searchQuery);
      const baseSimilarity = 0.35 + (typePriority[chunkType] ?? 0) * 0.05 + kwScore * 0.08;
      return toResult({ ...row, similarity: Math.min(baseSimilarity, 0.95) });
    })
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, matchCount);
}

/** Ensure every file in results has at least one body (chunk) row in the prompt. */
async function expandSearchResultsWithBodyChunks(
  owner: string,
  repo: string,
  userId: string,
  results: RepoChunkResult[]
): Promise<RepoChunkResult[]> {
  const paths = [
    ...new Set(
      results
        .map(r => r.file_path)
        .filter(p => p && p !== '[REPO_TREE]' && p !== 'README.md')
    ),
  ];
  if (paths.length === 0) return results;

  const hasBodyForPath = (path: string) =>
    results.some(
      r => r.file_path === path && getChunkType(r) === 'chunk' && !r.chunk_text.includes('[SIGNATURES]')
    );

  const pathsNeedingBody = paths.filter(p => !hasBodyForPath(p));
  if (pathsNeedingBody.length === 0) return results;

  const { data, error } = await supabase
    .from('github_repo_chunks')
    .select('file_path, chunk_index, chunk_text, metadata, chunk_type')
    .eq('user_id', userId)
    .eq('owner', owner)
    .eq('repo', repo)
    .in('file_path', pathsNeedingBody);

  if (error) {
    console.warn('[Repo RAG] expandSearchResultsWithBodyChunks failed:', error.message);
    return results;
  }

  const bodyChunks: RepoChunkResult[] = (data || [])
    .filter((row: Record<string, unknown>) => {
      const text = row.chunk_text as string;
      const type = getChunkType({
        file_path: row.file_path as string,
        chunk_text: text,
        metadata: row.metadata as Record<string, unknown>,
        chunk_type: row.chunk_type as string | undefined,
      });
      return type === 'chunk' && !text.includes('[SIGNATURES]');
    })
    .map((row: Record<string, unknown>) => ({
      file_path: row.file_path as string,
      chunk_index: row.chunk_index as number,
      chunk_text: row.chunk_text as string,
      similarity: 0.35,
      metadata: row.metadata as Record<string, unknown>,
    }));

  const seen = new Set(results.map(rrfChunkKey));
  const merged = [...results];
  for (const b of bodyChunks) {
    const k = rrfChunkKey(b);
    if (!seen.has(k)) {
      seen.add(k);
      merged.push(b);
    }
  }
  return merged;
}

const MAX_RECONSTRUCT_FILE_BYTES = 50_000;

/** Parse file paths from the indexed [REPO_TREE] chunk (no GitHub call). */
export async function getRepoTreePathsFromIndex(
  owner: string,
  repo: string
): Promise<string[]> {
  const userId = await getCurrentUserId();
  if (!userId || !isSupabaseConfigured) return [];

  const { data, error } = await supabase
    .from('github_repo_chunks')
    .select('chunk_text')
    .eq('user_id', userId)
    .eq('owner', owner)
    .eq('repo', repo)
    .eq('file_path', '[REPO_TREE]')
    .limit(1)
    .maybeSingle();

  if (error || !data?.chunk_text) return [];

  const text = String(data.chunk_text);
  const marker = 'File structure:\n';
  const start = text.indexOf(marker);
  if (start < 0) return [];

  return text
    .slice(start + marker.length)
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
}

/** Distinct file paths that have body chunks in the repo index. */
export async function listIndexedFilePaths(owner: string, repo: string): Promise<string[]> {
  const userId = await getCurrentUserId();
  if (!userId || !isSupabaseConfigured) return [];

  const { data, error } = await supabase
    .from('github_repo_chunks')
    .select('file_path, chunk_type, chunk_text')
    .eq('user_id', userId)
    .eq('owner', owner)
    .eq('repo', repo);

  if (error || !data?.length) return [];

  const paths = new Set<string>();
  for (const row of data as Array<{ file_path: string; chunk_type?: string; chunk_text?: string }>) {
    if (row.file_path === '[REPO_TREE]' || row.file_path === 'README.md') continue;
    const type = getChunkType({
      file_path: row.file_path,
      chunk_text: row.chunk_text ?? '',
      chunk_type: row.chunk_type,
    });
    if (type === 'chunk' && !(row.chunk_text ?? '').includes('[SIGNATURES]')) {
      paths.add(row.file_path);
    }
  }
  return [...paths];
}

export interface ReconstructedRepoFile {
  path: string;
  content: string;
  truncated: boolean;
}

/**
 * Reconstruct full file content from indexed repo chunks (fallback when GitHub fetch fails).
 */
export async function reconstructFilesFromChunks(
  owner: string,
  repo: string,
  paths: string[]
): Promise<ReconstructedRepoFile[]> {
  const userId = await getCurrentUserId();
  if (!userId || !isSupabaseConfigured || paths.length === 0) return [];

  const { data, error } = await supabase
    .from('github_repo_chunks')
    .select('file_path, chunk_index, chunk_text, metadata, chunk_type')
    .eq('user_id', userId)
    .eq('owner', owner)
    .eq('repo', repo)
    .in('file_path', paths);

  if (error || !data?.length) {
    if (error) console.warn('[Repo RAG] reconstructFilesFromChunks failed:', error.message);
    return [];
  }

  const byPath = new Map<string, Array<{ chunk_index: number; chunk_text: string }>>();
  for (const row of data as Record<string, unknown>[]) {
    const filePath = row.file_path as string;
    const chunkText = row.chunk_text as string;
    const chunkType = getChunkType({
      file_path: filePath,
      chunk_text: chunkText,
      metadata: row.metadata as Record<string, unknown>,
      chunk_type: row.chunk_type as string | undefined,
    });
    if (chunkType !== 'chunk' || chunkText.includes('[SIGNATURES]')) continue;

    const list = byPath.get(filePath) ?? [];
    list.push({ chunk_index: row.chunk_index as number, chunk_text: chunkText });
    byPath.set(filePath, list);
  }

  const results: ReconstructedRepoFile[] = [];
  for (const path of paths) {
    const chunks = byPath.get(path);
    if (!chunks?.length) continue;

    chunks.sort((a, b) => a.chunk_index - b.chunk_index);
    const content = chunks.map(c => c.chunk_text).join('\n');
    if (!content.trim()) continue;

    const byteSize = new TextEncoder().encode(content).length;
    if (byteSize > MAX_RECONSTRUCT_FILE_BYTES) {
      results.push({
        path,
        content: content.slice(0, MAX_RECONSTRUCT_FILE_BYTES),
        truncated: true,
      });
    } else {
      results.push({ path, content, truncated: false });
    }
  }

  return results;
}

/**
 * 3-pass hybrid search: Find (tree+signatures) → Filter (code chunks) → Feed (FTS)
 * Merged via RRF for optimal ranking.
 */
export async function searchRepo(
  query: string,
  owner: string,
  repo: string,
  options: {
    limit?: number;
    similarityThreshold?: number;
    hybridSearch?: boolean;
  } = {}
): Promise<RepoChunkResult[]> {
  const { limit = 10, similarityThreshold = 0.4, hybridSearch = true } = options;
  const userId = await getCurrentUserId();
  if (!userId || !query?.trim()) return [];
  if (!isSupabaseConfigured) return [];

  const embeddingResult = await embeddingService.generateEmbedding(query.trim());
  const queryVector = embeddingResult.embedding;
  if (!Array.isArray(queryVector) || queryVector.length === 0) {
    console.warn('[Repo RAG] searchRepo: empty embedding returned');
    return [];
  }
  const fetchCount = Math.max(limit * 2, 20);

  const toResult = (row: Record<string, unknown>): RepoChunkResult => ({
    file_path: row.file_path as string,
    chunk_index: row.chunk_index as number,
    chunk_text: row.chunk_text as string,
    similarity: (row.similarity as number) ?? (row.rank as number) ?? 0,
    metadata: row.metadata as Record<string, unknown>,
  });

  const skeletonResults = await matchRepoChunksByType(
    queryVector,
    userId,
    owner,
    repo,
    ['tree', 'signature'],
    Math.max(0.3, similarityThreshold - 0.05),
    Math.ceil(fetchCount / 2),
    query.trim()
  );

  const codeResults = await matchRepoChunksByType(
    queryVector,
    userId,
    owner,
    repo,
    ['chunk'],
    similarityThreshold,
    fetchCount,
    query.trim()
  );

  if (!hybridSearch) {
    const merged = mergeRepoChunksWithRRF(skeletonResults, codeResults, 60);
    const expanded = await expandSearchResultsWithBodyChunks(owner, repo, userId, merged);
    return expanded.slice(0, limit);
  }

  const { data: ftsData, error: ftsError } = await supabase.rpc('search_github_repo_fts', {
    query_text: query.trim(),
    filter_user_id: userId,
    filter_owner: owner,
    filter_repo: repo,
    match_count: fetchCount,
  });
  if (ftsError) {
    console.warn('[Repo RAG] search_github_repo_fts failed:', ftsError.message);
  }
  const ftsResults: RepoChunkResult[] = (ftsData || []).map(toResult);

  const codeMerged = mergeRepoChunksWithRRF(codeResults, ftsResults, 60);
  const all = mergeRepoChunksWithRRF(skeletonResults, codeMerged, 60);
  const expanded = await expandSearchResultsWithBodyChunks(owner, repo, userId, all);
  return expanded.slice(0, Math.max(limit, 15));
}

export type SelectedRepoRef = { owner: string; name: string; fullName: string };

/**
 * Search across multiple attached repos; results include owner/repo for disambiguation.
 */
export async function searchReposMulti(
  query: string,
  repos: SelectedRepoRef[],
  options: {
    limit?: number;
    similarityThreshold?: number;
    hybridSearch?: boolean;
  } = {}
): Promise<RepoChunkResult[]> {
  if (!repos.length) return [];
  const perRepoLimit = Math.max(5, Math.ceil((options.limit ?? 10) / repos.length));
  const all: RepoChunkResult[] = [];

  for (const r of repos) {
    const hits = await searchRepo(query, r.owner, r.name, {
      ...options,
      limit: perRepoLimit,
    });
    for (const hit of hits) {
      all.push({
        ...hit,
        owner: r.owner,
        repo: r.name,
        metadata: {
          ...hit.metadata,
          repo_full_name: r.fullName,
        },
      });
    }
  }

  return all
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, options.limit ?? 10);
}

function mergeRepoChunksWithRRF(
  listA: RepoChunkResult[],
  listB: RepoChunkResult[],
  k = 60
): RepoChunkResult[] {
  const scoreMap = new Map<string, { result: RepoChunkResult; score: number }>();
  const key = rrfChunkKey;
  const add = (results: RepoChunkResult[]) => {
    results.forEach((r, i) => {
      const rrf = 1 / (k + i + 1);
      const id = key(r);
      const existing = scoreMap.get(id);
      if (existing) existing.score += rrf;
      else scoreMap.set(id, { result: r, score: rrf });
    });
  };
  add(listA);
  add(listB);
  return Array.from(scoreMap.values())
    .sort((a, b) => b.score - a.score)
    .map(({ result }) => result);
}

/** Check if a repo has been fully indexed (code chunks + completed job). */
const PASS1_FILE_PATHS = ['[REPO_TREE]', 'README.md'];
const MIN_CODE_CHUNKS_COMPLETE = 5;

export type RepoIndexStatus = 'none' | 'pass1Only' | 'partial' | 'complete';

export interface RepoIndexStatusResult {
  status: RepoIndexStatus;
  totalChunks: number;
  codeChunks: number;
  hasCompletedJob: boolean;
  symbolsStatus?: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped' | null;
}

export async function getRepoIndexStatus(
  owner: string,
  repo: string
): Promise<RepoIndexStatusResult> {
  const userId = await getCurrentUserId();
  if (!userId || !isSupabaseConfigured) {
    return { status: 'none', totalChunks: 0, codeChunks: 0, hasCompletedJob: false, symbolsStatus: null };
  }

  const targetId = `${owner}/${repo}`;

  const [totalRes, codeRes, jobRes] = await Promise.all([
    supabase
      .from('github_repo_chunks')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('owner', owner)
      .eq('repo', repo),
    supabase
      .from('github_repo_chunks')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('owner', owner)
      .eq('repo', repo)
      .neq('file_path', '[REPO_TREE]')
      .neq('file_path', 'README.md'),
    supabase
      .from('embedding_jobs')
      .select('id, status, progress, symbols_status')
      .eq('user_id', userId)
      .eq('job_type', 'repo')
      .eq('target_id', targetId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);

  const totalChunks = totalRes.error ? 0 : (totalRes.count ?? 0);
  const codeChunks = codeRes.error ? 0 : (codeRes.count ?? 0);
  const jobMarkedComplete =
    jobRes.data?.status === 'completed' ||
    (jobRes.data?.progress as { phase?: string } | undefined)?.phase === 'done';
  const hasCompletedJob = jobMarkedComplete && codeChunks >= MIN_CODE_CHUNKS_COMPLETE;

  let status: RepoIndexStatus = 'none';
  if (hasCompletedJob || codeChunks >= MIN_CODE_CHUNKS_COMPLETE) {
    status = 'complete';
  } else if (codeChunks > 0) {
    status = 'partial';
  } else if (totalChunks > 0) {
    status = 'pass1Only';
  }

  return {
    status,
    totalChunks,
    codeChunks,
    hasCompletedJob,
    symbolsStatus: (jobRes.data?.symbols_status as RepoIndexStatusResult['symbolsStatus']) ?? null,
  };
}

export async function isRepoFullyIndexed(owner: string, repo: string): Promise<boolean> {
  const { status } = await getRepoIndexStatus(owner, repo);
  return status === 'complete';
}

/** @deprecated Prefer isRepoFullyIndexed — true only when code chunks exist or job completed. */
export async function isRepoIndexed(owner: string, repo: string): Promise<boolean> {
  return isRepoFullyIndexed(owner, repo);
}
